package com.exura.features.newauth.data.repository

import com.exura.features.newauth.domain.model.AuthUser
import com.exura.features.newauth.domain.repository.AuthRepository
import dev.gitlive.firebase.auth.FirebaseAuth
import dev.gitlive.firebase.auth.FirebaseUser
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.map

/**
 * Base implementation of AuthRepository using Firebase Authentication.
 * Handles common authentication operations for any user type.
 */
abstract class FirebaseAuthRepository<T: AuthUser>(
    private val auth: FirebaseAuth
) : AuthRepository {

    override val currentUser: Flow<T?> = auth.authStateChanged.map { firebaseUser ->
        firebaseUser?.let { convertToAuthUser(it) }
    }

    override suspend fun register(email: String, password: String): Result<T> = runCatching {
        val userCredential = auth.createUserWithEmailAndPassword(email, password)
        convertToAuthUser(userCredential.user!!)
    }

    override suspend fun login(email: String, password: String): Result<T> = runCatching {
        val userCredential = auth.signInWithEmailAndPassword(email, password)
        convertToAuthUser(userCredential.user!!)
    }


    suspend fun signInToken(token: String): Result<T> = runCatching {
        val firebaseUser = auth.signInWithCustomToken(token)
        convertToAuthUser(firebaseUser.user!!)
    }


    override suspend fun logout(): Result<Unit> = runCatching {
        auth.signOut()
    }

    override suspend fun sendPasswordResetEmail(email: String): Result<Unit> = runCatching {
        auth.sendPasswordResetEmail(email)
    }

    /**
     * Convert a Firebase user to an AuthUser.
     * This method should be overridden by subclasses to provide specific user type conversion.
     */
    abstract suspend fun convertToAuthUser(firebaseUser: FirebaseUser): T
}
